import type { FixtureFile } from "./fixtures";

export interface ReviewBookmark {
  id: string;
  repository: string;
  pullNumber: number;
  headSha: string;
  path: string;
  side: "LEFT" | "RIGHT";
  line: number;
  lineText: string;
  createdAt: string;
}

export type BookmarkFreshness = "current" | "needs-review" | "missing" | "loading";

export function bookmarkId(path: string, side: "LEFT" | "RIGHT", line: number): string {
  return `${side}:${path}:${line}`;
}

export function bookmarkStorageKey(repository: string, pullNumber: number): string {
  return `chew:bookmarks:${repository}#${pullNumber}:v1`;
}

export function loadBookmarks(key: string): ReviewBookmark[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((bookmark): bookmark is ReviewBookmark => Boolean(
      bookmark &&
      typeof bookmark === "object" &&
      "id" in bookmark && typeof bookmark.id === "string" &&
      "repository" in bookmark && typeof bookmark.repository === "string" &&
      "pullNumber" in bookmark && typeof bookmark.pullNumber === "number" &&
      "headSha" in bookmark && typeof bookmark.headSha === "string" &&
      "path" in bookmark && typeof bookmark.path === "string" &&
      "side" in bookmark && (bookmark.side === "LEFT" || bookmark.side === "RIGHT") &&
      "line" in bookmark && typeof bookmark.line === "number" &&
      "lineText" in bookmark && typeof bookmark.lineText === "string" &&
      "createdAt" in bookmark && typeof bookmark.createdAt === "string",
    ));
  } catch {
    return [];
  }
}

export function saveBookmarks(key: string, bookmarks: ReviewBookmark[]): void {
  localStorage.setItem(key, JSON.stringify(bookmarks));
}

export function lineTextAt(file: FixtureFile | undefined, side: "LEFT" | "RIGHT", line: number): string | undefined {
  if (!file || file.loading || file.binary) return undefined;
  return (side === "LEFT" ? file.base : file.head).split("\n")[line - 1];
}

export function bookmarkFreshness(
  bookmark: ReviewBookmark,
  currentHeadSha: string,
  file: FixtureFile | undefined,
): BookmarkFreshness {
  if (file?.loading) return "loading";
  const currentText = lineTextAt(file, bookmark.side, bookmark.line);
  if (currentText === undefined || currentText !== bookmark.lineText) return "missing";
  return bookmark.headSha === currentHeadSha ? "current" : "needs-review";
}
